import React, { useState, useRef, useEffect } from "react"
import { Card, CardBody, Button, Spinner } from "reactstrap"
import { Search, Gamepad2, ArrowLeft, GitCompare, CheckCircle, PlusCircle } from "lucide-react"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import SEO from "../../components/Common/SEO"
import KeywordSuggestionsDetailsModal from "../../components/Common/Modal/KeywordSuggestionsDetailsModal"
import CompetitorsModal from "../../components/Common/Modal/CompetitorsModal"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateSlug, generateCanonicalUrl } from "../../utils/seoUtils"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import { api } from '../../utils/variables';
import { fetchKeywordSuggestions, normalizeKeywordItems } from '../../utils/keywordSuggestionApi';
import { useSearchProgress } from "../../hooks/useSearchProgress";
import SearchProgressModal from "../../components/Common/Modal/SearchProgressModal";
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics";

const KeywordSuggestions = () => {
    const navigate = useNavigate()
    const { trackEvent } = useAnalytics()
    const searchInputRef = useRef(null)

    const [searchTerm, setSearchTerm] = useState("")
    const [loading, setLoading] = useState(false)
    const [showDetailsModal, setDetailsShowModal] = useState(false)
    const [isHovered, setIsHovered] = useState(false)

    // Keyword display state
    const [selectedApp, setSelectedApp] = useState(null)
    const [fetchedKeywords, setFetchedKeywords] = useState([])
    const [fetchedKeywordsData, setFetchedKeywordsData] = useState([])
    // const [allKeywords, setAllKeywords] = useState([])
    const [selectedKeywords, setSelectedKeywords] = useState([])
    const [keywordsLoading, setKeywordsLoading] = useState(false)
    const [keywordsStillProcessing, setKeywordsStillProcessing] = useState(false)
    const [moreKeywordsLoading, setMoreKeywordsLoading] = useState(false)
    const [noMoreKeywords, setNoMoreKeywords] = useState(false)
    const [trackingLoading, setTrackingLoading] = useState(false)
    const [activeTab, setActiveTab] = useState('top') // 'top' or 'all'

    // Comparison state
    const [showCompareModal, setShowCompareModal] = useState(false)
    const [comparisonApps, setComparisonApps] = useState([])

    // Upgrade plan modal state
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)

    // Comparison tracking state
    const [trackedAppIds, setTrackedAppIds] = useState(() => {
        try {
            const stored = localStorage.getItem('compared_tracked_app_ids')
            return stored ? new Set(JSON.parse(stored)) : new Set()
        } catch { return new Set() }
    })
    const [comparisonTrackingLoading, setComparisonTrackingLoading] = useState(new Set())

    // Competitors modal state
    const [showCompetitorsModal, setShowCompetitorsModal] = useState(false)
    const [selectedCompetitors, setSelectedCompetitors] = useState([])
    const [selectedKeywordForCompetitors, setSelectedKeywordForCompetitors] = useState('')

    // Search progress modal state
    const { progress, showProgressModal, cancel: cancelSearchProgress } = useSearchProgress({
        loading: keywordsLoading || trackingLoading,
    });

    // Cross-source tracking detection: whenever comparison modal opens,
    // fetch all tracked apps from the API to detect keywords tracked from any source.
    useEffect(() => {
        if (!showCompareModal || comparisonApps.length === 0) return

        const checkTrackedApps = async () => {
            try {
                const response = await axios.get(
                    `${api}/tracker/tracking/get_user_tracked_keywords`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
                        },
                    }
                )
                const gameData = response.data?.data?.game_data || []
                const apiTrackedIds = new Set(gameData.map(g => String(g.item_id)))

                // Find which comparison apps are tracked
                const newlyTracked = comparisonApps
                    .map(a => String(a.item_id))
                    .filter(id => apiTrackedIds.has(id))

                if (newlyTracked.length > 0) {
                    setTrackedAppIds(prev => {
                        const next = new Set([...prev, ...newlyTracked])
                        try {
                            localStorage.setItem('compared_tracked_app_ids', JSON.stringify([...next]))
                        } catch { }
                        return next
                    })
                }
            } catch (err) {
                console.error('Failed to check tracked apps:', err)
            }
        }

        checkTrackedApps()
    }, [showCompareModal]) // eslint-disable-line react-hooks/exhaustive-deps

    // Listen for keyword tracking updates from other pages (e.g., when deleted from tracking landing page)
    useEffect(() => {
        const handleKeywordTrackingUpdated = (event) => {
            const { deletedAppId } = event.detail
            if (deletedAppId) {
                setTrackedAppIds(prev => {
                    const next = new Set(prev)
                    next.delete(String(deletedAppId))
                    try {
                        localStorage.setItem('compared_tracked_app_ids', JSON.stringify([...next]))
                    } catch { }
                    return next
                })
            }
        }

        window.addEventListener('keywordTrackingUpdated', handleKeywordTrackingUpdated)

        return () => {
            window.removeEventListener('keywordTrackingUpdated', handleKeywordTrackingUpdated)
        }
    }, [])

    const handleSearch = async () => {
        if (!searchTerm.trim()) return

        setLoading(true)
        // Note: The modal does its own search, but we trigger it here to open the modal with results
        setTimeout(() => {
            setLoading(false)
            setDetailsShowModal(true)
        }, 300)
    }

    const handleAppSelect = async (app) => {
        setSelectedApp(app)
        setFetchedKeywords([])
        setFetchedKeywordsData([])
        setSelectedKeywords([])
        setKeywordsStillProcessing(false)
        setNoMoreKeywords(false)
        setKeywordsLoading(true)
        setDetailsShowModal(false) // Close modal

        const gameId = app.item_id || app._id

        trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_APP_SELECTED, {
            app_id: gameId,
            app_name: app.name,
        })

        try {
            const data = await fetchKeywordSuggestions(gameId)

            const validKeywords = normalizeKeywordItems(data?.keywords)

            if (validKeywords.length > 0) {
                setFetchedKeywordsData(validKeywords)
                setFetchedKeywords(validKeywords.map(item => item.keyword))
            } else if (data?.status === "processing") {
                setKeywordsStillProcessing(true)
                toastr.info("Still generating keywords for this app - please try again in a moment.")
            } else {
                toastr.info("No keywords found for this app.")
            }
        } catch (error) {
            console.error("Error fetching keywords:", error)
            toastr.error("Failed to fetch keywords.")
        } finally {
            setKeywordsLoading(false)
        }
    }

    const handleShowMoreKeywords = async () => {
        if (!selectedApp) return

        const gameId = selectedApp.item_id || selectedApp._id
        setMoreKeywordsLoading(true)

        try {
            const data = await fetchKeywordSuggestions(gameId, { more: true })
            const moreKeywords = normalizeKeywordItems(data?.keywords)

            const existingKeywordSet = new Set(fetchedKeywords)
            const newItems = moreKeywords.filter(item => !existingKeywordSet.has(item.keyword))

            if (newItems.length > 0) {
                setFetchedKeywordsData(prev => [...prev, ...newItems])
                setFetchedKeywords(prev => [...prev, ...newItems.map(item => item.keyword)])
                toastr.success(`Loaded ${newItems.length} more keyword${newItems.length !== 1 ? 's' : ''}!`)
            } else if (data?.status === "processing") {
                toastr.info("Still generating more keywords for this app - please try again in a moment.")
            } else {
                setNoMoreKeywords(true)
                toastr.info("No more keywords found for this app.")
            }
        } catch (error) {
            console.error("Error fetching more keywords:", error)
            toastr.error("Failed to fetch more keywords.")
        } finally {
            setMoreKeywordsLoading(false)
        }
    }

    // API returns HTTP 200 but body has { status: 400, action: "UPGRADE" } when limit is hit
    const isLimitReachedResponse = (response) => {
        const data = response?.data || response || {}
        return (
            data.status === 400 &&
            (data.action === "UPGRADE" || /max limit/i.test(data.message || ""))
        )
    }

    // Fallback: also catch HTTP-level 400 errors thrown by axios
    const isLimitReachedError = (error) => {
        const errData = error.response?.data || {}
        return (
            error.response?.status === 400 &&
            (errData.action === "UPGRADE" || /max limit/i.test(errData.message || ""))
        )
    }

    const handleTrackSelected = async () => {
        if (selectedKeywords.length === 0 || !selectedApp) return

        setTrackingLoading(true)
        const gameId = selectedApp.item_id || selectedApp._id
        let successCount = 0
        let limitReached = false

        for (const kw of selectedKeywords) {
            try {
                const response = await axios.post(
                    `${api}/tracker/tracking/add_keyword_tracking`,
                    {
                        item_id: gameId,
                        keyword: kw,
                        country: "US",
                        language: "english",
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        },
                    }
                )
                if (isLimitReachedResponse(response)) {
                    limitReached = true
                } else {
                    successCount++
                }
            } catch (error) {
                if (isLimitReachedError(error)) {
                    limitReached = true
                } else {
                    toastr.error(`Failed to track ${kw}: ${error.response?.data?.message || error.message}`)
                }
            }
        }

        setTrackingLoading(false)

        if (limitReached) {
            setShowUpgradeModal(true)
            return
        }

        if (successCount > 0) {
            trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_TRACKING_ADDED, {
                app_id: gameId,
                app_name: selectedApp?.name,
                keyword_count: successCount,
                keywords: selectedKeywords.join(", "),
            })
            toastr.success(`Successfully tracked ${successCount} keywords!`)
            sessionStorage.setItem("firstAppKeyword", JSON.stringify(selectedApp))
            const dashboardUrl = `/xr/keyword/${generateSlug(selectedApp.name)}?id=${gameId}`
            navigate(dashboardUrl)
        } else {
            toastr.error("No keywords were tracked successfully.")
        }
    }

    const handleBackToSearch = () => {
        setSelectedApp(null)
        setFetchedKeywords([])
        setFetchedKeywordsData([])
        // setAllKeywords([])
        setSelectedKeywords([])
        setKeywordsStillProcessing(false)
        setNoMoreKeywords(false)
        setActiveTab('top')
    }

    const toggleKeywordSelection = (keyword) => {
        setSelectedKeywords(prev => {
            const isSelected = prev.includes(keyword)
            trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_KEYWORD_TOGGLED, {
                keyword,
                selected: !isSelected,
            })
            return isSelected ? prev.filter(k => k !== keyword) : [...prev, keyword]
        })
    }

    const toggleSelectAll = () => {
        if (selectedKeywords.length === fetchedKeywords.length) {
            setSelectedKeywords([])
            trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_SELECT_ALL_TOGGLED, {
                select_all: false,
            })
        } else {
            setSelectedKeywords([...fetchedKeywords])
            trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_SELECT_ALL_TOGGLED, {
                select_all: true,
            })
        }
    }

    // Comparison functions
    const handleAddToComparison = () => {
        if (!selectedApp || fetchedKeywords.length === 0) return

        trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_COMPARE_ADDED, {
            app_id: selectedApp.item_id || selectedApp._id,
        })

        const existingAppIndex = comparisonApps.findIndex(app => app.item_id === selectedApp.item_id)
        if (existingAppIndex !== -1) {
            // Update existing app
            const updatedApps = [...comparisonApps]
            updatedApps[existingAppIndex] = {
                ...selectedApp,
                keywords: fetchedKeywords,
                keywordsData: fetchedKeywordsData
            }
            setComparisonApps(updatedApps)
            toastr.info("App updated in comparison")
        } else {
            // Add new app
            setComparisonApps([...comparisonApps, {
                ...selectedApp,
                keywords: fetchedKeywords,
                keywordsData: fetchedKeywordsData
            }])
            toastr.success("App added to comparison")
        }
    }

    const handleCompetitorsView = (keyword, competitors) => {
        setSelectedKeywordForCompetitors(keyword)
        setSelectedCompetitors(competitors)
        setShowCompetitorsModal(true)
    }

    const handleCloseCompetitorsModal = () => {
        setShowCompetitorsModal(false)
        setSelectedCompetitors([])
        setSelectedKeywordForCompetitors('')
    }

    const handleRemoveFromComparison = (appId) => {
        trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_COMPARE_REMOVED, {
            app_id: appId,
        })
        setComparisonApps(comparisonApps.filter(app => app.item_id !== appId))
    }

    const handleComparisonAppSelect = async (app) => {
        const gameId = app.item_id || app._id

        try {
            const data = await fetchKeywordSuggestions(gameId)

            // Only keep entries that actually have a keyword string - a malformed/partial
            // API response should never add an app with an empty keyword list to the comparison.
            const validKeywords = normalizeKeywordItems(data?.keywords)
            const keywords = validKeywords.map(item => item.keyword)

            if (validKeywords.length > 0) {
                // Use functional update to avoid race conditions
                setComparisonApps(prevApps => {
                    const existingAppIndex = prevApps.findIndex(compApp => compApp.item_id === app.item_id)

                    if (existingAppIndex !== -1) {
                        // Update existing app
                        const updatedApps = [...prevApps]
                        updatedApps[existingAppIndex] = {
                            ...app,
                            keywords: keywords,
                            keywordsData: validKeywords
                        }
                        toastr.info("App updated in comparison")
                        return updatedApps
                    } else {
                        // Add new app
                        toastr.success("App added to comparison")
                        return [...prevApps, {
                            ...app,
                            keywords: keywords,
                            keywordsData: validKeywords
                        }]
                    }
                })
            } else if (data?.status === "processing") {
                toastr.info("Still generating keywords for this app - please try again in a moment.")
            } else {
                toastr.info("No keywords found for this app.")
            }
        } catch (error) {
            console.error("Error fetching comparison keywords:", error)
            toastr.error("Failed to fetch keywords.")
        }
    }

    // Handle multiple apps from modal comparison
    const handleCompareAppsFromModal = async (apps) => {
        // Process all apps concurrently to avoid race conditions
        try {
            const promises = apps.map(app => handleComparisonAppSelect(app))
            await Promise.all(promises)
            // Open comparison modal right after adding all apps
            setShowCompareModal(true)
        } catch (error) {
            console.error("Error adding apps to comparison:", error)
        }
    }

    const getAllUniqueKeywords = () => {
        const allKeywords = new Set()
        comparisonApps.forEach(app => {
            (app.keywords || []).forEach(keyword => keyword && allKeywords.add(keyword))
        })
        return Array.from(allKeywords).sort()
    }

    const isAppAlreadyTracked = (app) => {
        const id = String(app.item_id || app._id)
        return trackedAppIds.has(id)
    }

    const handleTrackAllForApp = async (app) => {
        if (!app || !app.keywords?.length) return

        const gameId = app.item_id || app._id
        const appId = String(gameId)
        setComparisonTrackingLoading(prev => new Set([...prev, appId]))

        let successCount = 0
        let limitReached = false
        for (const kw of app.keywords) {
            try {
                const response = await axios.post(
                    `${api}/tracker/tracking/add_keyword_tracking`,
                    {
                        item_id: gameId,
                        keyword: kw,
                        country: "US",
                        language: "english",
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        },
                    }
                )
                if (isLimitReachedResponse(response)) {
                    limitReached = true
                } else {
                    successCount++
                }
            } catch (error) {
                if (isLimitReachedError(error)) {
                    limitReached = true
                } else {
                    toastr.error(`Failed to track "${kw}": ${error.response?.data?.message || error.message}`)
                }
            }
        }

        setComparisonTrackingLoading(prev => {
            const next = new Set(prev)
            next.delete(appId)
            return next
        })

        if (limitReached) {
            setShowUpgradeModal(true)
            return
        }

        if (successCount > 0) {
            trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_TRACKING_ADDED, {
                app_id: gameId,
                app_name: app.name,
                keyword_count: successCount,
                keywords: app.keywords.join(", "),
            })
            setTrackedAppIds(prev => {
                const next = new Set([...prev, appId])
                try {
                    localStorage.setItem('compared_tracked_app_ids', JSON.stringify([...next]))
                } catch { }
                return next
            })
            toastr.success(`Successfully tracked ${successCount} keyword${successCount !== 1 ? 's' : ''}!`)
            sessionStorage.setItem("firstAppKeyword", JSON.stringify(app))
            navigate(`/xr/keyword/${generateSlug(app.name)}?id=${gameId}`)
        } else {
            toastr.error("No keywords were tracked successfully.")
        }
    }

    const addButtonStyle = {
        padding: "8px 16px",
        borderRadius: "10px",
        background: isHovered ? "#e11d4c" : "#F6255A",
        color: "#FFFFFF",
        border: "none",
        fontWeight: "600",
        cursor: "pointer",
        whiteSpace: "nowrap",
        transition: "all 0.2s ease-in-out",
        height: "48px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px"
    }

    return (
        <React.Fragment>
            <SEO
                title={PAGE_SEO.keywordSuggestions.title}
                description={PAGE_SEO.keywordSuggestions.description}
                keywords={PAGE_SEO.keywordSuggestions.keywords}
                canonical={generateCanonicalUrl('/keyword-suggestions')}
                robots={PAGE_SEO.keywordSuggestions.robots}
            />
            <SearchProgressModal
                show={showProgressModal}
                progress={progress}
                title="Generating keyword suggestions"
                onCancel={() => { cancelSearchProgress(); setDetailsShowModal(false); }}
                hasResults={fetchedKeywordsData.length > 0}
            />
            <div className="page-content">
                <div className="container-fluid page-content-suggestions">
                    <style>
                        {`
                            .page-content-suggestions {
                                font-family: 'Rubik', sans-serif !important;
                            }
                            .custom-checkbox-pink .form-check-input:checked {
                                background-color: #F6255A !important;
                                border-color: #F6255A !important;
                            }
                            .custom-checkbox-pink .form-check-input:focus {
                                border-color: #F6255A !important;
                                box-shadow: 0 0 0 0.25rem rgba(246, 37, 90, 0.25) !important;
                            }
                            @media (max-width: 576px) {
                                .key_track_search {
                                    flex-direction: column !important;
                                    align-items: stretch !important;
                                }
                                .searchField {
                                    max-width: 100% !important;
                                    width: 100% !important;
                                }
                                .searchBtn {
                                    width: 100% !important;
                                }
                                .key_track_sec {
                                    padding: 20px !important;
                                }
                            }
                        `}
                    </style>

                    <div className="mb-4 mt-2">
                        <div className="d-flex align-items-center gap-3 mb-2">
                            <h1 className="mb-0 text-white" style={{ fontWeight: 700, fontSize: '28px' }}>Keyword Suggestions</h1>
                        </div>
                        <p style={{ fontSize: '15px', maxWidth: '1000px', color: '#e8e8e8', fontWeight: 400 }}>
                            Discover high-performing keywords for your apps using our advanced engine.
                            Browse competitor strategies and optimize your app store presence with data-driven suggestions.
                        </p>
                    </div>

                    <div className="key_track_sec" style={{
                        height: 'auto',
                        minHeight: 'unset',
                        padding: '30px',
                        background: '#21222d',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        borderRadius: '20px'
                    }}>
                        <div className="mb-4">
                            <h2 className="text-white mb-2" style={{ fontWeight: 600 }}>Identify App for Suggestions</h2>
                            <p style={{ fontSize: '14px', color: '#e8e8e8' }}>Search for any app to generate its most relevant and impactful keywords.</p>
                        </div>

                        <div className="key_track_search d-flex align-items-center flex-column flex-sm-row gap-3 mb-3">
                            <div className="key_track_search_inn searchField d-flex align-items-center flex-grow-1" style={{
                                // maxWidth: '700px',
                                background: '#21222d',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                borderRadius: '12px'
                            }}>
                                <i className="fa-solid fa-magnifying-glass ms-3" style={{ color: '#adb5bd', position: 'static' }}></i>
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Enter App or Game name"
                                    className="flex-grow-1"
                                    style={{ background: 'transparent', border: 'none', color: '#fff', padding: '12px', fontFamily: 'Rubik', minWidth: '0' }}
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === "Enter") {
                                            handleSearch()
                                        }
                                    }}
                                />
                            </div>
                            <button
                                className="searchBtn"
                                onClick={handleSearch}
                                onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}
                                style={{ ...addButtonStyle, width: "120px", height: "44px", fontSize: '15px', fontFamily: 'Rubik' }}
                            >
                                {loading ? <Spinner size="sm" /> : <Search size={16} className="me-2" />}
                                Search
                            </button>
                            {comparisonApps.length > 0 && (
                                <button
                                    className="btn ms-auto"
                                    style={{
                                        background: 'linear-gradient(135deg, #F6255A 0%, #E91E63 100%)',
                                        color: '#fff',
                                        padding: '10px 18px',
                                        borderRadius: '50px',
                                        fontWeight: '600',
                                        border: 'none',
                                        boxShadow: '0 4px 16px rgba(246, 37, 90, 0.4)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        fontFamily: 'Rubik',
                                        fontSize: '14px',
                                        height: '44px',
                                        minWidth: '160px'
                                    }}
                                    onClick={() => {
                                        trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_SUGGESTIONS_COMPARE_OPENED, {
                                            compare_count: comparisonApps.length,
                                        })
                                        setShowCompareModal(true)
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-1px)';
                                        e.target.style.boxShadow = '0 6px 20px rgba(246, 37, 90, 0.5)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 4px 16px rgba(246, 37, 90, 0.4)';
                                    }}
                                >
                                    <GitCompare size={16} className="me-2" />
                                    Compare ({comparisonApps.length})
                                </button>
                            )}
                        </div>
                    </div>

                    {!selectedApp ? (
                        <div className="mt-4 text-center py-5" style={{
                            background: "#21222d",
                            borderRadius: "24px",
                            border: "1px dashed rgba(255,255,255,0.1)"
                        }}>
                            <div style={{ maxWidth: "500px", margin: "0 auto" }}>
                                <div style={{
                                    width: "80px",
                                    height: "80px",
                                    background: "rgba(246, 37, 90, 0.05)",
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    margin: "0 auto 24px",
                                    border: '1px solid rgba(246, 37, 90, 0.2)'
                                }}>
                                    <Gamepad2 size={32} color="#F6255A" />
                                </div>
                                <h2 className="text-white mb-3" style={{ fontSize: '21.6px' }}>Discovery Mode Ready</h2>
                                <p style={{ lineHeight: '1.6', color: '#e8e8e8' }} className="mb-4">
                                    Your personal assistant is ready to analyze the market.
                                    Select an app above to reveal the keywords that matter most in your category.
                                </p>
                                <Button
                                    onClick={() => setDetailsShowModal(true)}
                                    style={{
                                        background: "rgba(246, 37, 90, 0.1)",
                                        border: "1px solid #F6255A",
                                        color: "#F6255A",
                                        padding: "12px 40px",
                                        borderRadius: "12px",
                                        fontWeight: "600",
                                        transition: 'all 0.3s ease',
                                        fontFamily: 'Rubik'
                                    }}
                                    className="hover-scale"
                                >
                                    Start Discovery
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-4">
                            <Card className="border-0" style={{ background: "#21222d", borderRadius: "16px" }}>
                                <CardBody className="p-4">
                                    <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <button
                                                onClick={handleBackToSearch}
                                                className="btn btn-sm"
                                                style={{ background: "rgba(255,255,255,0.05)", border: "none", color: "#fff", borderRadius: "8px", padding: "8px 12px" }}
                                            >
                                                <ArrowLeft size={18} />
                                            </button>
                                            <img
                                                src={selectedApp.image_url}
                                                alt={selectedApp.name}
                                                style={{ width: '56px', height: '56px', borderRadius: '12px', objectFit: 'cover' }}
                                            />
                                            <div style={{ padding: '4px 0' }}>
                                                <h2 className="text-white mb-1" style={{ fontWeight: 600, fontSize: '20px' }}>{selectedApp.name}</h2>
                                                {/* <p style={{ fontSize: '14px', color: '#e8e8e8' }} className="mb-0">AI-Powered Keyword Suggestions</p> */}
                                            </div>
                                        </div>
                                    </div>

                                    {keywordsLoading ? (
                                        <div className="text-center py-5">
                                            <Spinner style={{ color: '#F6255A' }} />
                                            <p className="mt-3 text-white" style={{ fontFamily: 'Rubik' }}>Generating keywords...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {activeTab === 'top' && (
                                                <>
                                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                                                        <div>
                                                            <h3 className="mb-1 text-white" style={{ fontWeight: 600, fontSize: '18px' }}>
                                                                Top Keywords
                                                            </h3>
                                                            <p style={{ fontSize: '13px', color: '#e8e8e8' }} className="mb-0">
                                                                {fetchedKeywords.length} Generated keywords found
                                                            </p>
                                                        </div>
                                                        <div className="d-flex gap-2 flex-wrap">
                                                            <button
                                                                className="btn w-auto"
                                                                onClick={handleAddToComparison}
                                                                disabled={fetchedKeywords.length === 0}
                                                                style={{
                                                                    background: fetchedKeywords.length > 0 ? "#F6255A" : "#2A2C38",
                                                                    color: "#fff",
                                                                    padding: "10px 16px",
                                                                    borderRadius: "10px",
                                                                    fontWeight: "600",
                                                                    border: "none",
                                                                    boxShadow: fetchedKeywords.length > 0 ? "0 4px 12px rgba(246, 37, 90, 0.3)" : "none",
                                                                    transition: "all 0.2s ease",
                                                                    fontFamily: 'Rubik'
                                                                }}
                                                            >
                                                                <GitCompare size={16} className="me-2" />
                                                                Add to Compare
                                                            </button>
                                                            <button
                                                                className="btn w-auto"
                                                                disabled={selectedKeywords.length === 0 || trackingLoading}
                                                                onClick={handleTrackSelected}
                                                                style={{
                                                                    background: selectedKeywords.length > 0 ? "#F6255A" : "#2A2C38",
                                                                    color: "#fff",
                                                                    padding: "10px 16px",
                                                                    borderRadius: "10px",
                                                                    fontWeight: "600",
                                                                    border: "none",
                                                                    boxShadow: selectedKeywords.length > 0 ? "0 4px 12px rgba(246, 37, 90, 0.3)" : "none",
                                                                    transition: "all 0.2s ease",
                                                                    fontFamily: 'Rubik'
                                                                }}
                                                            >
                                                                {trackingLoading ? (
                                                                    <>
                                                                        <Spinner size="sm" className="me-2" />
                                                                        Tracking...
                                                                    </>
                                                                ) : (
                                                                    `Track Selected (${selectedKeywords.length})`
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div className="table-responsive mb-4">
                                                        <table className="table table-nowrap align-middle mb-0" style={{ background: 'transparent', minWidth: '700px' }}>
                                                            <thead>
                                                                <tr style={{
                                                                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                                                                    background: 'rgba(255,255,255,0.02)'
                                                                }}>
                                                                    <th style={{
                                                                        width: '50px',
                                                                        padding: '16px 20px',
                                                                        borderBottom: 'none'
                                                                    }}>
                                                                        <div className="form-check font-size-16 custom-checkbox-pink">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                checked={fetchedKeywords.length > 0 && selectedKeywords.length === fetchedKeywords.length}
                                                                                onChange={toggleSelectAll}
                                                                                id="checkAll"
                                                                                style={{ cursor: 'pointer' }}
                                                                            />
                                                                            <label className="form-check-label" htmlFor="checkAll"></label>
                                                                        </div>
                                                                    </th>
                                                                    <th style={{
                                                                        fontSize: '13px',
                                                                        fontWeight: 600,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px',
                                                                        padding: '16px 20px',
                                                                        borderBottom: 'none',
                                                                        color: '#adb5bd'
                                                                    }}>
                                                                        Keyword
                                                                    </th>
                                                                    {/* Total Downloads is no longer returned by the keyword-suggestion API - commented out for now */}
                                                                    {/* <th className="text-end" style={{
                                                                        fontSize: '13px',
                                                                        fontWeight: 600,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px',
                                                                        padding: '16px 20px',
                                                                        borderBottom: 'none',
                                                                        color: '#adb5bd'
                                                                    }}>
                                                                        <div
                                                                            className="d-flex align-items-center justify-content-end gap-2"
                                                                            style={{ cursor: 'pointer' }}
                                                                            onClick={handleSortToggle}
                                                                        >
                                                                            Total Downloads
                                                                            {sortOrder === 'desc' ? (
                                                                                <ArrowDown size={14} style={{ color: '#F6255A' }} />
                                                                            ) : (
                                                                                <ArrowUp size={14} style={{ color: '#F6255A' }} />
                                                                            )}
                                                                        </div>
                                                                    </th> */}
                                                                    <th className="text-center" style={{
                                                                        fontSize: '13px',
                                                                        fontWeight: 600,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px',
                                                                        padding: '16px 20px',
                                                                        borderBottom: 'none',
                                                                        color: '#adb5bd'
                                                                    }}>
                                                                        Rank
                                                                    </th>
                                                                    <th className="text-center" style={{
                                                                        fontSize: '13px',
                                                                        fontWeight: 600,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px',
                                                                        padding: '16px 20px',
                                                                        borderBottom: 'none',
                                                                        color: '#adb5bd'
                                                                    }}>
                                                                        Competitors
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {fetchedKeywords.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan={4} style={{ padding: '32px 20px', textAlign: 'center', color: '#9CA3AF', borderBottom: 'none' }}>
                                                                            {keywordsStillProcessing
                                                                                ? "Still generating keywords for this app - please check back in a moment."
                                                                                : "No keywords found for this app."}
                                                                        </td>
                                                                    </tr>
                                                                ) : fetchedKeywords.map((kw, idx) => {
                                                                    const isSelected = selectedKeywords.includes(kw)
                                                                    const keywordData = fetchedKeywordsData.find(item => item.keyword === kw)
                                                                    const competitors = keywordData?.competitors || []

                                                                    return (
                                                                        <tr
                                                                            key={idx}
                                                                            style={{
                                                                                borderBottom: idx === fetchedKeywords.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.04)',
                                                                                cursor: 'pointer',
                                                                                background: isSelected ? 'rgba(246, 37, 90, 0.08)' : 'transparent',
                                                                                transition: 'all 0.2s ease'
                                                                            }}
                                                                            onClick={() => toggleKeywordSelection(kw)}
                                                                            onMouseEnter={(e) => {
                                                                                if (!isSelected) {
                                                                                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)'
                                                                                }
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                if (!isSelected) {
                                                                                    e.currentTarget.style.background = 'transparent'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <td style={{ padding: '16px 20px', borderBottom: 'none' }}>
                                                                                <div className="form-check font-size-16 custom-checkbox-pink">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input"
                                                                                        checked={isSelected}
                                                                                        onChange={() => toggleKeywordSelection(kw)}
                                                                                        id={`kw-check-${idx}`}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        style={{ cursor: 'pointer' }}
                                                                                    />
                                                                                    <label className="form-check-label" htmlFor={`kw-check-${idx}`}></label>
                                                                                </div>
                                                                            </td>
                                                                            <td style={{ padding: '16px 20px', borderBottom: 'none' }}>
                                                                                <span className="text-white" style={{
                                                                                    fontSize: '15px',
                                                                                    fontWeight: 500,
                                                                                    letterSpacing: '0.2px'
                                                                                }}>
                                                                                    {kw}
                                                                                </span>
                                                                            </td>
                                                                            {/* Total Downloads is no longer returned by the keyword-suggestion API - commented out for now */}
                                                                            {/* <td className="text-end" style={{ padding: '16px 20px', borderBottom: 'none' }}>
                                                                                <span style={{
                                                                                    background: 'rgba(246, 37, 90, 0.1)',
                                                                                    color: '#F6255A',
                                                                                    fontSize: '12px',
                                                                                    fontWeight: 600,
                                                                                    padding: '6px 12px',
                                                                                    borderRadius: '8px',
                                                                                    display: 'inline-block',
                                                                                    width: '72px',
                                                                                    textAlign: 'center'
                                                                                }}>
                                                                                    {keywordData ? formatDownloads(keywordData.demand) : '-'}
                                                                                </span>
                                                                            </td> */}
                                                                            <td className="text-center" style={{ padding: '16px 20px', borderBottom: 'none' }}>
                                                                                {keywordData?.rank ? (
                                                                                    <span style={{ color: '#e8e8e8', fontSize: '13px', fontWeight: 500 }}>
                                                                                        #{keywordData.rank}
                                                                                        {keywordData.out_of ? (
                                                                                            <span style={{ color: '#6b7280' }}> / {keywordData.out_of}</span>
                                                                                        ) : null}
                                                                                    </span>
                                                                                ) : (
                                                                                    <span style={{ color: '#9CA3AF', fontSize: '12px' }}>-</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="text-center" style={{ padding: '16px 20px', borderBottom: 'none' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                                                                                    {competitors.length === 0 ? (
                                                                                        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>-</span>
                                                                                    ) : (
                                                                                        <>
                                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                                {competitors.slice(0, 5).map((competitor, i) => (
                                                                                                    <div
                                                                                                        key={competitor.item_id}
                                                                                                        title={competitor.name}
                                                                                                        style={{
                                                                                                            width: '32px',
                                                                                                            height: '32px',
                                                                                                            borderRadius: '50%',
                                                                                                            border: '2px solid #1e2028',
                                                                                                            marginLeft: i === 0 ? 0 : '-12px',
                                                                                                            overflow: 'hidden',
                                                                                                            zIndex: 5 - i,
                                                                                                            backgroundColor: '#2C2D33',
                                                                                                            display: 'flex',
                                                                                                            alignItems: 'center',
                                                                                                            justifyContent: 'center',
                                                                                                            fontSize: '12px',
                                                                                                            fontWeight: 600,
                                                                                                            color: '#fff'
                                                                                                        }}
                                                                                                    >
                                                                                                        {competitor.image_url ? (
                                                                                                            <img
                                                                                                                src={competitor.image_url}
                                                                                                                alt={competitor.name}
                                                                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                                            />
                                                                                                        ) : (
                                                                                                            competitor.name ? competitor.name.charAt(0).toUpperCase() : '?'
                                                                                                        )}
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation()
                                                                                                    handleCompetitorsView(kw, competitors)
                                                                                                }}
                                                                                                style={{
                                                                                                    background: 'linear-gradient(135deg, #5A5A5A 0%, #C49598 100%)',
                                                                                                    color: '#FFFFFF',
                                                                                                    border: 'none',
                                                                                                    borderRadius: '4px',
                                                                                                    padding: '4px 10px',
                                                                                                    fontSize: '11px',
                                                                                                    fontWeight: 'bold',
                                                                                                    cursor: 'pointer',
                                                                                                    transition: 'all 0.2s ease',
                                                                                                    boxShadow: '0 2px 4px rgba(128, 128, 128, 0.3)',
                                                                                                    whiteSpace: 'nowrap'
                                                                                                }}
                                                                                                onMouseEnter={e => {
                                                                                                    e.target.style.background = 'linear-gradient(135deg, #4A4A4A 0%, #B08588 100%)'
                                                                                                    e.target.style.transform = 'translateY(-1px)'
                                                                                                    e.target.style.boxShadow = '0 4px 8px rgba(128, 128, 128, 0.4)'
                                                                                                }}
                                                                                                onMouseLeave={e => {
                                                                                                    e.target.style.background = 'linear-gradient(135deg, #5A5A5A 0%, #C49598 100%)'
                                                                                                    e.target.style.transform = 'translateY(0)'
                                                                                                    e.target.style.boxShadow = '0 2px 4px rgba(128, 128, 128, 0.3)'
                                                                                                }}
                                                                                            >
                                                                                                View
                                                                                            </button>
                                                                                        </>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {fetchedKeywords.length > 0 && (
                                                        <div className="text-center mb-2">
                                                            <button
                                                                className="btn w-auto"
                                                                onClick={handleShowMoreKeywords}
                                                                disabled={moreKeywordsLoading || noMoreKeywords}
                                                                style={{
                                                                    background: 'rgba(246, 37, 90, 0.1)',
                                                                    border: '1px solid #F6255A',
                                                                    color: '#F6255A',
                                                                    padding: '10px 24px',
                                                                    borderRadius: '10px',
                                                                    fontWeight: '600',
                                                                    opacity: noMoreKeywords ? 0.5 : 1,
                                                                    transition: 'all 0.2s ease',
                                                                    fontFamily: 'Rubik'
                                                                }}
                                                            >
                                                                {moreKeywordsLoading ? (
                                                                    <>
                                                                        <Spinner size="sm" className="me-2" />
                                                                        Loading...
                                                                    </>
                                                                ) : noMoreKeywords ? (
                                                                    'All Keywords Loaded'
                                                                ) : (
                                                                    <>
                                                                        <PlusCircle size={16} className="me-2" />
                                                                        Show More Keywords
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* All Keywords Tab Content - Commented out */}
                                            {/* {activeTab === 'all' && (
                                                <>
                                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
                                                        <div>
                                                            <h6 className="mb-1 text-white" style={{ fontWeight: 600, fontSize: '18px' }}>
                                                                All Keywords
                                                            </h6>
                                                            <p style={{ fontSize: '13px', color: '#e8e8e8' }} className="mb-0">
                                                                {allKeywords.length} Total keywords found
                                                            </p>
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                className="btn w-auto"
                                                                onClick={handleAddToComparison}
                                                                disabled={allKeywords.length === 0}
                                                                style={{
                                                                    background: allKeywords.length > 0 ? "#F6255A" : "#2A2C38",
                                                                    color: "#fff",
                                                                    padding: "10px 16px",
                                                                    borderRadius: "10px",
                                                                    fontWeight: "600",
                                                                    border: "none",
                                                                    boxShadow: allKeywords.length > 0 ? "0 4px 12px rgba(246, 37, 90, 0.3)" : "none",
                                                                    transition: "all 0.2s ease",
                                                                    fontFamily: 'Rubik'
                                                                }}
                                                            >
                                                                <GitCompare size={16} className="me-2" />
                                                                Add to Compare
                                                            </button>
                                                            <button
                                                                className="btn w-auto"
                                                                disabled={selectedKeywords.length === 0 || trackingLoading}
                                                                onClick={handleTrackSelected}
                                                                style={{
                                                                    background: selectedKeywords.length > 0 ? "#F6255A" : "#2A2C38",
                                                                    color: "#fff",
                                                                    padding: "10px 16px",
                                                                    borderRadius: "10px",
                                                                    fontWeight: "600",
                                                                    border: "none",
                                                                    boxShadow: selectedKeywords.length > 0 ? "0 4px 12px rgba(246, 37, 90, 0.3)" : "none",
                                                                    transition: "all 0.2s ease",
                                                                    fontFamily: 'Rubik'
                                                                }}
                                                            >
                                                                {trackingLoading ? (
                                                                    <>
                                                                        <Spinner size="sm" className="me-2" />
                                                                        Tracking...
                                                                    </>
                                                                ) : (
                                                                    `Track Selected (${selectedKeywords.length})`
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="table-responsive mb-4">
                                                        <table className="table table-nowrap align-middle mb-0" style={{ background: 'transparent', minWidth: '600px' }}>
                                                            <thead>
                                                                <tr style={{
                                                                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                                                                    background: 'rgba(255,255,255,0.02)'
                                                                }}>
                                                                    <th style={{
                                                                        width: '50px',
                                                                        padding: '16px 20px',
                                                                        borderBottom: 'none'
                                                                    }}>
                                                                        <div className="form-check font-size-16 custom-checkbox-pink">
                                                                            <input
                                                                                type="checkbox"
                                                                                className="form-check-input"
                                                                                checked={allKeywords.length > 0 && selectedKeywords.length === allKeywords.length}
                                                                                onChange={() => {
                                                                                    if (selectedKeywords.length === allKeywords.length) {
                                                                                        setSelectedKeywords([])
                                                                                    } else {
                                                                                        setSelectedKeywords([...allKeywords])
                                                                                    }
                                                                                }}
                                                                                id="checkAllKeywords"
                                                                                style={{ cursor: 'pointer' }}
                                                                            />
                                                                            <label className="form-check-label" htmlFor="checkAllKeywords"></label>
                                                                        </div>
                                                                    </th>
                                                                    <th style={{
                                                                        fontSize: '13px',
                                                                        fontWeight: 600,
                                                                        textTransform: 'uppercase',
                                                                        letterSpacing: '0.5px',
                                                                        padding: '16px 20px',
                                                                        borderBottom: 'none',
                                                                        color: '#adb5bd'
                                                                    }}>
                                                                        Keyword
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {allKeywords.map((keyword, idx) => {
                                                                    const isSelected = selectedKeywords.includes(keyword)
                                                                    return (
                                                                        <tr
                                                                            key={idx}
                                                                            style={{
                                                                                borderBottom: idx === allKeywords.length - 1 ? 'none' : '1px solid rgba(255,255,255,0.04)',
                                                                                cursor: 'pointer',
                                                                                background: isSelected ? 'rgba(246, 37, 90, 0.08)' : 'transparent',
                                                                                transition: 'all 0.2s ease'
                                                                            }}
                                                                            onClick={() => toggleKeywordSelection(keyword)}
                                                                            onMouseEnter={(e) => {
                                                                                if (!isSelected) {
                                                                                    e.currentTarget.style.background = 'rgba(255,255,255,0.03)'
                                                                                }
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                if (!isSelected) {
                                                                                    e.currentTarget.style.background = 'transparent'
                                                                                }
                                                                            }}
                                                                        >
                                                                            <td style={{ padding: '16px 20px', borderBottom: 'none' }}>
                                                                                <div className="form-check font-size-16 custom-checkbox-pink">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input"
                                                                                        checked={isSelected}
                                                                                        onChange={() => toggleKeywordSelection(keyword)}
                                                                                        id={`all-kw-check-${idx}`}
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        style={{ cursor: 'pointer' }}
                                                                                    />
                                                                                    <label className="form-check-label" htmlFor={`all-kw-check-${idx}`}></label>
                                                                                </div>
                                                                            </td>
                                                                            <td style={{ padding: '16px 20px', borderBottom: 'none' }}>
                                                                                <div className="d-flex align-items-center gap-2">
                                                                                    <span className="text-white" style={{
                                                                                        fontSize: '15px',
                                                                                        fontWeight: 500,
                                                                                        letterSpacing: '0.2px'
                                                                                    }}>
                                                                                        {keyword}
                                                                                    </span>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )} */}
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </div>
                    )}
                </div>


                {/* Comparison Modal */}
                {showCompareModal && (
                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
                        <div className="modal-dialog modal-xl" style={{ margin: '50px auto' }}>
                            <div className="modal-content" style={{ background: '#21222d', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px' }}>
                                <div className="modal-header border-0 p-4">
                                    <div className="d-flex align-items-center justify-content-between w-100">
                                        <div className="d-flex align-items-center gap-3">
                                            <div>
                                                <h4 className="text-white mb-1" style={{ fontWeight: 600 }}>Keyword Comparison</h4>
                                                <p style={{ fontSize: '14px', color: '#e8e8e8' }} className="mb-0">
                                                    Compare keywords across {comparisonApps.length} apps
                                                </p>
                                            </div>
                                            {/* {comparisonApps.length > 0 && comparisonApps.length < 5 && (
                                                <div className="d-flex align-items-center" style={{ opacity: 0.7 }}>
                                                    <span style={{ color: '#adb5bd', fontSize: '12px', marginRight: '8px' }}>
                                                        Add more apps to compare
                                                    </span>
                                                </div>
                                            )} */}
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <button
                                                className="btn"
                                                onClick={() => setDetailsShowModal(true)}
                                                style={{
                                                    background: 'rgba(246, 37, 90, 0.1)',
                                                    border: '1px solid #F6255A',
                                                    color: '#F6255A',
                                                    padding: '8px 16px',
                                                    borderRadius: '8px',
                                                    fontWeight: '600',
                                                    fontFamily: 'Rubik',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                <Search size={16} className="me-2" />
                                                Add App
                                            </button>
                                            <button
                                                type="button"
                                                className="btn-close"
                                                onClick={() => setShowCompareModal(false)}
                                                style={{
                                                    fontSize: '20px',
                                                    opacity: 0.8,
                                                    filter: 'brightness(0) invert(1)',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '50%',
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.opacity = '1';
                                                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.opacity = '0.8';
                                                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                                }}
                                            ></button>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-body p-4">
                                    {comparisonApps.length === 0 ? (
                                        <div className="text-center py-5">
                                            <GitCompare size={48} color="#F6255A" className="mb-3" />
                                            <h5 className="text-white mb-2">No Apps to Compare</h5>
                                            <p style={{ color: '#e8e8e8' }}>Add apps to compare their keywords side by side.</p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table" style={{ background: 'transparent' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ color: '#adb5bd', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '16px' }}>Keyword</th>
                                                        {comparisonApps.map((app, idx) => (
                                                            <th key={idx} style={{ color: '#adb5bd', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '16px', minWidth: '200px' }}>
                                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                                    <img src={app.image_url} alt={app.name} style={{ width: '24px', height: '24px', borderRadius: '6px' }} />
                                                                    <span className="text-white">{app.name}</span>
                                                                    <button
                                                                        className="btn btn-sm ms-auto"
                                                                        onClick={() => handleRemoveFromComparison(app.item_id)}
                                                                        style={{ background: 'rgba(244, 63, 94, 0.1)', border: 'none', color: '#f43f5e', padding: '4px 8px', borderRadius: '4px' }}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                                {isAppAlreadyTracked(app) ? (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#10b981', fontSize: '11px', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal' }}>
                                                                        <CheckCircle size={12} />
                                                                        Keywords Already Tracked
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => handleTrackAllForApp(app)}
                                                                        disabled={comparisonTrackingLoading.has(String(app.item_id))}
                                                                        style={{
                                                                            background: comparisonTrackingLoading.has(String(app.item_id))
                                                                                ? 'rgba(246,37,90,0.5)'
                                                                                : 'linear-gradient(135deg, #F6255A 0%, #E91E63 100%)',
                                                                            color: '#fff',
                                                                            border: 'none',
                                                                            borderRadius: '6px',
                                                                            padding: '5px 10px',
                                                                            fontSize: '11px',
                                                                            fontWeight: 600,
                                                                            cursor: comparisonTrackingLoading.has(String(app.item_id)) ? 'not-allowed' : 'pointer',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            textTransform: 'none',
                                                                            letterSpacing: 'normal',
                                                                            fontFamily: 'Rubik'
                                                                        }}
                                                                    >
                                                                        {comparisonTrackingLoading.has(String(app.item_id)) ? (
                                                                            <><Spinner size="sm" /> Tracking...</>
                                                                        ) : (
                                                                            <><CheckCircle size={12} /> Track Keywords</>
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getAllUniqueKeywords().length === 0 ? (
                                                        <tr>
                                                            <td colSpan={comparisonApps.length + 1} style={{ padding: '32px 16px', textAlign: 'center', color: '#9CA3AF' }}>
                                                                No keywords found for the selected apps.
                                                            </td>
                                                        </tr>
                                                    ) : getAllUniqueKeywords().map((keyword, idx) => (
                                                        <tr key={idx} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                                            <td style={{ padding: '16px', color: '#fff', fontWeight: 500 }}>{keyword}</td>
                                                            {comparisonApps.map((app, appIdx) => {
                                                                const hasKeyword = (app.keywords || []).includes(keyword)
                                                                return (
                                                                    <td key={appIdx} style={{ padding: '16px', textAlign: 'center' }}>
                                                                        {hasKeyword ? (
                                                                            <span style={{
                                                                                background: 'rgba(246, 37, 90, 0.1)',
                                                                                color: '#F6255A',
                                                                                fontSize: '12px',
                                                                                fontWeight: 600,
                                                                                padding: '4px 8px',
                                                                                borderRadius: '6px',
                                                                                display: 'inline-block'
                                                                            }}>
                                                                                ✓
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ color: '#6b7280', fontSize: '14px' }}>—</span>
                                                                        )}
                                                                    </td>
                                                                )
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <AppsTrackingPlanModal
                    show={showUpgradeModal}
                    handleClose={() => setShowUpgradeModal(false)}
                    title="Upgrade your plan to unlock this feature"
                />

                <KeywordSuggestionsDetailsModal
                    show={showDetailsModal}
                    handleClose={() => setDetailsShowModal(false)}
                    title={showCompareModal ? "Add Apps to Compare" : "Search Apps"}
                    searchTerm={searchTerm}
                    onSearchChange={e => setSearchTerm(e.target.value)}
                    onSearchClick={() => { }} // Modal handles its own mutation
                    onSelectApp={showCompareModal ? handleComparisonAppSelect : handleAppSelect}
                    selectButtonLabel={showCompareModal ? "Add to Comparison" : "View Keywords"}
                    comparisonMode={showCompareModal}
                    onCompareApps={handleCompareAppsFromModal}
                    comparisonApps={comparisonApps}
                />

                <CompetitorsModal
                    show={showCompetitorsModal}
                    handleClose={handleCloseCompetitorsModal}
                    competitors={selectedCompetitors}
                    keyword={selectedKeywordForCompetitors}
                />
            </div>
        </React.Fragment>
    )
}

export default KeywordSuggestions
